import { memo } from 'react';
import {
  type InteractionColors,
  type InteractionOptions,
} from '@components/Presentation/types';
import OptionOrderType from './optionOrderType';
import { CheckRounded, ClearRounded } from '@mui/icons-material';
import useFonts from '@lib/hooks/useFonts';
import { computeLuminance } from '@lib/interactionHelper';
import { colorPalette } from '@lib/constants';
import AvatarBadge from '@components/AvatarBadge';
import { Tooltip } from '@heroui/react';
import { getInitials } from '@services/utils.service';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';

interface IOptions extends InteractionOptions {
  contribution?: string;
  count?: string;
  profileNames?: string[];
  otherCommenters?: number;
}

const MultipleChoiceOptions = ({
  option,
  settings,
  index,
  relativeMax,
  showAnswer,
  colors,
  totalOptionCount = 2,
  showResponses = true,
  fontsize = 'text-4xl',
  roundedCorner = 'rounded-[40px]',
  optionNumContainer = 'w-[100px]',
  parentContainer = { height: 'h-full' }
}: {
  option: IOptions;
  settings: any;
  index: number;
  relativeMax: number;
  showAnswer: boolean;
  colors?: InteractionColors;
  totalOptionCount?: number;
  showResponses?: boolean;
  fontsize?: string;
  roundedCorner?: string;
  optionNumContainer?: string;
  parentContainer?: {
    height?: string;
  }
}) => {
  const contribution = !showResponses ? 0 : parseInt(option?.contribution || '0', 10);
  const count = !showResponses ? 0 : parseInt(option?.count || '0', 10);
  const computedFontSize = useFontSizeCalculator(settings?.multipleChoiceFontSize ? Number(settings?.multipleChoiceFontSize) : 1, 1.8, 'rem');

  let percent = 0;
  if (relativeMax && !isNaN(contribution)) {
    percent = (contribution / relativeMax) * 100;
  }

  const width = !showResponses ? '0%' : `${percent}%`;

  const contributionText =
    settings?.DisplayStyle === 'percentage'
      ? `${contribution}%`
      : `${count} votes`;

  const { getFontStyle } = useFonts();
  const fontStyle = getFontStyle(settings?.FontStyle || 'Sans-Serif');

  const { isBgLight } = computeLuminance(colors?.multipleChoiceBgColor as any || '#111111');

  return (
    <div className={`flex items-center gap-3 max-h-[200px] justify-center py-2 h-full z-2 ${parentContainer.height}`}>
      <div
        className={`transition-all duration-500 ease-linear ${
          showAnswer ? 'max-w-16' : 'max-w-0 opacity-0'
        } overflow-hidden`}
      >
        {option.checked ? (
          <CheckRounded className={`${isBgLight ? 'text-black' : 'text-white'}`} style={{ fontSize: '3rem' }} />
        ) : (
          <ClearRounded
            className={`${isBgLight ? 'text-black' : 'text-white'} opacity-50`}
            style={{ fontSize: '3rem' }}
          />
        )}
      </div>
      <OptionOrderType
        index={index}
        optionNumContainer={optionNumContainer}
        prefix={settings?.prefix}
        type={settings?.optionOrderType}
        chartColor={colors?.multipleChoiceChartColor}
        textColor={colors?.multipleChoiceLabelColor}
      />

      <div className={`w-full h-full flex overflow-hidden  ${roundedCorner}`}>
        <div className="w-full h-full flex items-center relative">

          {/* Filled section */}
          <div
            style={{
              width,
              background: colors?.multipleChoiceChartColor || '#E8634D',
            }}
            className="absolute left-0 top-0 h-full z-0 transition-all ease-linear duration-1000"
          ></div>

          {/* Lightened section (background base) */}
          <div
            style={{
              width: '100%',
              left: 0,
              background: colors?.multipleChoiceChartColor || '#E8634D',
              opacity: 0.5,
            }}
            className="absolute top-0 h-full z-0"
          ></div>

          {/* Foreground content */}
          <div className="relative z-10 flex w-full h-full items-center">
            <div className="flex justify-between items-center w-full px-4 sm:px-8">

              {/* Option Text (left side) */}
              <span
                style={{
                  ...fontStyle,
                  fontSize: computedFontSize,
                  color: colors?.multipleChoiceLabelColor || '#FFF',
                }}
                className="multipleChoiceOptionText font-Inter 2xl:text-4xl xl:text-3xl text-3xl font-semibold text-wrap"
              >
                {option.body}
              </span>

              <div className="flex items-center gap-3">
                {/* Avatars */}
                {settings?.multipleChoiceShowProfilePictures && (<div className="flex items-center relative">
                  {(option.profileNames ?? []).slice(0, 4).map((username, index) => {
                    return (
                      <Tooltip
                        key={index}
                        content={
                          <div className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded-full flex justify-center items-center text-[10px] font-semibold text-[#212121]"
                              style={{ backgroundColor: colorPalette[index % colorPalette.length] }}
                            >
                              {getInitials(username)}
                            </div>
                            <span className="text-sm font-normal text-[#212121]">{username}</span>
                          </div>
                        }

                        placement="top"
                        showArrow
                        classNames={{
                          base: ['before:bg-neutral-400 dark:before:bg-white before:bg-white'],
                          content: ['py-2 px-4 shadow-xl', 'text-black bg-white'],
                        }}
                      >
                        <div className={'flex flex-row-reverse'}>
                          <AvatarBadge name={getInitials(username)} index={index} />
                        </div>
                      </Tooltip>
                    );
                  })}

                  {(option.otherCommenters ?? 0) > 0 && (
                    <Tooltip
                      placement="top"
                      showArrow
                      classNames={{
                        base: ['before:bg-neutral-400 dark:before:bg-white before:bg-white'],
                        content: ['py-2 px-4 shadow-xl', 'text-black bg-white max-h-48 overflow-y-auto rounded-md', 'z-9999999'],
                      }}
                      content={
                        <div className="flex flex-col gap-2">
                          {(option.profileNames ?? [])
                            .slice(4)
                            .sort((a, b) => a.localeCompare(b))
                            .map((username, idx) => {
                              const initials = getInitials(username);
                              const bgColor = colorPalette[(4 + idx) % colorPalette.length];

                              return (
                                <div key={idx} className="flex items-center gap-2">
                                  <div
                                    className="w-6 h-6 rounded-full flex justify-center items-center text-[10px] font-semibold text-[#212121]"
                                    style={{ backgroundColor: bgColor }}
                                  >
                                    {initials}
                                  </div>
                                  <span className="text-sm font-normal text-[#212121]">{username}</span>
                                </div>
                              );
                            })}
                        </div>
                      }
                    >
                      <div
                        className="w-[38px] h-[38px] rounded-full flex justify-center items-center relative z-4"
                        style={{
                          backgroundColor: '#FFFFFF',
                          marginLeft: '0px',
                          zIndex: 9999,
                        }}
                      >
                        <div className="text-[#212121] text-sm font-semibold font-['Inter'] leading-tight">
                          {option.otherCommenters}+
                        </div>
                      </div>
                    </Tooltip>
                  )}
                </div>)}

                {/* Contribution % */}
                <span
                  style={{
                    ...fontStyle,
                    fontSize: computedFontSize,
                    color: colors?.multipleChoiceLabelColor || '#FFF',
                  }}
                  className="font-Inter 2xl:text-4xl xl:text-3xl text-3xl font-semibold opacity-70 pl-4"
                >
                  {contributionText}
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

    </div>
  );
};

export default memo(MultipleChoiceOptions);
